// Parse AIFF buffer and extract audio samples Supports 8, 16, 24, and 32-bit audio
export const parseAiffBuffer = (arrayBuffer: ArrayBuffer) => {
  const view = new DataView(arrayBuffer);

  // Verify FORM header
  const formHeader = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3)
  );

  if (formHeader !== "FORM") {
    throw new Error("Not a valid AIFF file (missing FORM header)");
  }

  // Verify AIFF type
  const aiffType = String.fromCharCode(
    view.getUint8(8),
    view.getUint8(9),
    view.getUint8(10),
    view.getUint8(11)
  );

  if (aiffType !== "AIFF" && aiffType !== "AIFC") {
    throw new Error("Not a valid AIFF file");
  }

  let offset = 12;
  let numChannels: number = 0;
  let sampleRate: number = 0;
  let numSampleFrames: number = 0;
  let bitsPerSample: number = 0;
  let soundDataOffset: number = 0;
  let soundDataSize: number = 0;
  let compressionType: string = "NONE"; // For AIFC files

  // Parse chunks
  while (offset < view.byteLength - 8) {
    const chunkID = String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3)
    );

    const chunkSize = view.getUint32(offset + 4, false); // Big-endian

    if (chunkID === "COMM") {
      numChannels = view.getUint16(offset + 8, false);
      numSampleFrames = view.getUint32(offset + 10, false);
      bitsPerSample = view.getUint16(offset + 14, false);

      // Parse 80-bit extended float sample rate
      const sampleRateBytes = new Uint8Array(arrayBuffer, offset + 16, 10);
      sampleRate = parseExtended80(sampleRateBytes);

      // Check if this is AIFC format (has compression type)
      if (aiffType === "AIFC" && chunkSize >= 22) {
        // Read compression type (4 bytes after the sample rate)
        compressionType = String.fromCharCode(
          view.getUint8(offset + 26),
          view.getUint8(offset + 27),
          view.getUint8(offset + 28),
          view.getUint8(offset + 29)
        );
      }
    } else if (chunkID === "SSND") {
      // SSND chunk structure: 4 bytes: chunk ID ("SSND") 4 bytes: chunk size 4 bytes: offset (usually 0) 4 bytes: block size (usually 0) remaining: actual audio data
      const ssndOffset = view.getUint32(offset + 8, false); // Offset field

      // Audio data starts after the 8-byte header (offset + blockSize fields)
      soundDataOffset = offset + 16 + ssndOffset;
      soundDataSize = chunkSize - 8; // Total chunk size minus the 8-byte header
    }

    offset += 8 + chunkSize;
    // AIFF chunks are padded to even boundaries
    if (chunkSize % 2 !== 0) {
      offset++;
    }
  }

  if (
    numChannels === 0 ||
    sampleRate === 0 ||
    numSampleFrames === 0 ||
    bitsPerSample === 0 ||
    soundDataOffset === 0
  ) {
    throw new Error("Could not find required AIFF chunks");
  }

  // Extract samples based on bit depth
  const samples = extractAiffSamples(
    arrayBuffer,
    soundDataOffset,
    soundDataSize,
    numSampleFrames,
    numChannels,
    bitsPerSample,
    compressionType
  );

  const duration = numSampleFrames / sampleRate;

  return {
    samples,
    sampleRate,
    numChannels,
    numSampleFrames,
    bitsPerSample,
    duration,
    compressionType,
  };
};

// Extract audio samples from AIFF data Properly handles 8, 16, 24, and 32-bit audio Supports both big-endian (standard AIFF) and little-endian (AIFC "sowt")
const extractAiffSamples = (
  arrayBuffer: ArrayBuffer,
  offset: number,
  size: number,
  numSampleFrames: number,
  numChannels: number,
  bitsPerSample: number,
  compressionType: string
): Float32Array => {
  const view = new DataView(arrayBuffer, offset, size);
  const samples = new Float32Array(numSampleFrames * numChannels);
  const bytesPerSample = Math.ceil(bitsPerSample / 8);

  // Determine byte order based on compression type "sowt" = little-endian, "NONE" or standard AIFF = big-endian
  const isLittleEndian = compressionType === "sowt";

  let sampleIndex = 0;

  for (let i = 0; i < numSampleFrames * numChannels; i++) {
    const byteOffset = i * bytesPerSample;

    if (bitsPerSample === 8) {
      // 8-bit is unsigned: 0-255, center at 128
      const sample = view.getUint8(byteOffset);
      samples[sampleIndex] = (sample - 128) / 128.0;
    } else if (bitsPerSample === 16) {
      // 16-bit signed (byte order depends on compression type)
      const sample = view.getInt16(byteOffset, isLittleEndian);
      samples[sampleIndex] = sample / 32768.0;
    } else if (bitsPerSample === 24) {
      // 24-bit signed (byte order depends on compression type)
      const byte1 = view.getUint8(byteOffset);
      const byte2 = view.getUint8(byteOffset + 1);
      const byte3 = view.getUint8(byteOffset + 2);

      // Combine bytes into 24-bit value (order depends on endianness)
      let sample;
      if (isLittleEndian) {
        sample = byte1 | (byte2 << 8) | (byte3 << 16);
      } else {
        sample = (byte1 << 16) | (byte2 << 8) | byte3;
      }

      // Sign extend from 24-bit to 32-bit Check if the sign bit (bit 23) is set
      if ((sample & 0x800000) !== 0) {
        // Sign extend by setting upper 8 bits to 1
        sample = sample | 0xff000000;
      }

      // Convert to signed 32-bit integer then normalize
      sample = sample | 0; // Force to signed 32-bit
      samples[sampleIndex] = sample / 8388608.0; // 2^23
    } else if (bitsPerSample === 32) {
      // 32-bit signed (byte order depends on compression type)
      const sample = view.getInt32(byteOffset, isLittleEndian);
      samples[sampleIndex] = sample / 2147483648.0;
    } else {
      throw new Error(`Unsupported bit depth: ${bitsPerSample}`);
    }

    sampleIndex++;
  }

  return samples;
};

// Parse 80-bit extended precision floating point (used for sample rate in AIFF)
const parseExtended80 = (bytes: Uint8Array): number => {
  const exponent = ((bytes[0] & 0x7f) << 8) | bytes[1];

  if (exponent === 0 && bytes[2] === 0) {
    return 0;
  }

  const mantissa =
    ((bytes[2] << 24) | (bytes[3] << 16) | (bytes[4] << 8) | bytes[5]) >>> 0;
  const value = mantissa * Math.pow(2, exponent - 16383 - 31);

  return (bytes[0] & 0x80) !== 0 ? -value : value;
};
